import React, { useCallback, useEffect } from 'react';
import { InvoiceDetailsModalProps } from './types';
import {
  InvoiceActionButtons,
  InvoiceAdminNote,
  InvoiceLineItems,
  InvoiceMetaGrid,
  InvoiceModalHeader,
} from './components';
import './InvoiceDetailsModal.scss';

export const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onDownloadPdf,
  onResendInvoice,
  isDownloading = false,
  isResending = false,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDownloading && !isResending) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isDownloading, isResending, onClose]);

  const handleDownload = useCallback(() => {
    if (invoice && onDownloadPdf) onDownloadPdf(invoice);
  }, [invoice, onDownloadPdf]);

  const handleResend = useCallback(() => {
    if (invoice && onResendInvoice) onResendInvoice(invoice);
  }, [invoice, onResendInvoice]);

  if (!isOpen || !invoice) return null;

  const isBusy = isDownloading || isResending;

  return (
    <div className="invoice-details-modal" role="presentation">
      <div
        className="invoice-details-modal__overlay"
        onClick={isBusy ? undefined : onClose}
        aria-hidden="true"
      />

      <div
        className="invoice-details-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-details-modal-title"
      >
        <InvoiceModalHeader
          invoiceId={invoice.invoiceId}
          onClose={onClose}
          disabled={isBusy}
        />

        <div className="invoice-details-modal__body">
          <InvoiceActionButtons
            onDownloadPdf={onDownloadPdf ? handleDownload : undefined}
            onResendInvoice={onResendInvoice ? handleResend : undefined}
            isDownloading={isDownloading}
            isResending={isResending}
          />

          <InvoiceMetaGrid
            clientName={invoice.clientName}
            issueDate={invoice.issueDate}
            invoiceId={invoice.invoiceId}
            status={invoice.status}
          />

          {invoice.noteFromAdmin?.trim() ? (
            <InvoiceAdminNote note={invoice.noteFromAdmin.trim()} />
          ) : null}

          <InvoiceLineItems
            items={invoice.lineItems}
            totalAmount={invoice.totalAmount}
            currency={invoice.currency}
          />
        </div>
      </div>
    </div>
  );
};
